/**
 * Document Signing Modal - Consumer Portal
 *
 * Right-side panel with embedded DocuSeal iframe for inline document signing.
 * Used by consumers to sign documents sent by business users.
 * Migrated to SplitPanel pattern per CLAUDE.md rule #14.
 *
 * Issue #300: E-Sign service integration with DocuSeal
 */

import {
  Box,
  GlassButton,
  ShimmerLoader,
  SplitPanel,
  SplitPanelContent,
  SplitPanelFooter,
  SplitPanelHeader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useEsignEmbedUrl } from '../../lib/trpc/hooks';

interface DocumentSigningModalProps {
  open: boolean;
  onClose: () => void;
  submitterId: number | null;
  signerName?: string;
  documentName?: string;
  onSigningComplete?: () => void;
}

export const DocumentSigningModal = ({
  open,
  onClose,
  submitterId,
  signerName,
  documentName,
  onSigningComplete,
}: DocumentSigningModalProps): ReactElement => {
  const { t } = useI18n();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [signingComplete, setSigningComplete] = useState(false);
  const [windowOpened, setWindowOpened] = useState(false);

  const { data: embedData, isLoading, error } = useEsignEmbedUrl(open ? submitterId : null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setIframeLoaded(false);
      setSigningComplete(false);
      setWindowOpened(false);
    }
  }, [open]);

  // Listen for DocuSeal postMessage events
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event: MessageEvent) => {
      // DocuSeal sends messages when signing is complete
      if (event.data?.type === 'docuseal:completed') {
        setSigningComplete(true);
        onSigningComplete?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open, onSigningComplete]);

  // Open DocuSeal in a new browser tab (iframe blocked by X-Frame-Options: SAMEORIGIN)
  useEffect(() => {
    if (open && embedData?.url && !windowOpened && !signingComplete) {
      window.open(embedData.url, '_blank', 'noopener');
      setWindowOpened(true);
      setIframeLoaded(true);
    }
  }, [open, embedData?.url, windowOpened, signingComplete]);

  const handleClose = useCallback(() => {
    if (signingComplete) {
      onSigningComplete?.();
    }
    onClose();
  }, [signingComplete, onSigningComplete, onClose]);

  return (
    <SplitPanel
      panelOpen={open}
      onPanelClose={handleClose}
      panel={
        <>
          <SplitPanelHeader
            title={signingComplete ? t('esign.signing.complete') : t('esign.signing.title')}
            subtitle={
              documentName
                ? t('esign.signing.documentLabel', { name: documentName })
                : signerName
                  ? t('esign.signing.signerLabel', { name: signerName })
                  : undefined
            }
            onClose={handleClose}
          />

          <SplitPanelContent padding={0}>
            <Box
              sx={{
                width: '100%',
                height: 'calc(100vh - 180px)',
                minHeight: 500,
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              {/* Loading state */}
              {(isLoading || !iframeLoaded) && !error && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                  }}
                >
                  <ShimmerLoader variant="circular" width={48} height={48} />
                  <Typography variant="body1" color="textSecondary">
                    {t('esign.signing.loading')}
                  </Typography>
                </Box>
              )}

              {/* Error state */}
              {error && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="h6" color="error">
                    {t('esign.signing.failed')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('esign.signing.failedHelp')}
                  </Typography>
                  <GlassButton onClick={onClose} variant="outlined">
                    {t('common.close')}
                  </GlassButton>
                </Box>
              )}

              {/* Signing complete state */}
              {signingComplete && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    bgcolor: 'background.paper',
                    zIndex: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h3" sx={{ color: 'white' }}>
                      ✓
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="success.main">
                    {t('esign.signing.success')}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {t('esign.signing.successHelp')}
                  </Typography>
                </Box>
              )}

              {/* Document opened in new tab */}
              {windowOpened && !signingComplete && !error && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <ShimmerLoader variant="circular" width={48} height={48} />
                  <Typography variant="h6" color="textPrimary">
                    {t('esign.signing.title')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                    {t('esign.signing.openedInNewTab')}
                  </Typography>
                  <GlassButton
                    variant="outlined"
                    size="small"
                    onClick={() => embedData?.url && window.open(embedData.url, '_blank', 'noopener')}
                  >
                    {t('esign.signing.reopenTab')}
                  </GlassButton>
                </Box>
              )}
            </Box>
          </SplitPanelContent>

          <SplitPanelFooter>
            {signingComplete ? (
              <GlassButton onClick={handleClose} variant="gradient" fullWidth>
                {t('esign.signing.done')}
              </GlassButton>
            ) : (
              <GlassButton onClick={handleClose} variant="outlined" fullWidth>
                {t('common.cancel')}
              </GlassButton>
            )}
          </SplitPanelFooter>
        </>
      }
    >
      <Box />
    </SplitPanel>
  );
};

export default DocumentSigningModal;
